/**
 * Service worker registration, done by the app instead of vite-plugin-pwa's injected
 * registerSW.js (vite.config.ts `injectRegister: false`) so it can depend on where the app runs
 * (spec 2026-10-13 §13):
 *
 * - A normal browser with the PWA: the same call the plugin's script made — register `/sw.js` at
 *   scope `/` once the page has loaded.
 * - Inside the native kiosk shell: none. The shell wipes the machine between customers and
 *   restarts nightly, and a cached old build is the bigger risk there, so any worker already
 *   installed in that profile is removed.
 */
type ServiceWorkerApi = Pick<ServiceWorkerContainer, 'register' | 'getRegistrations'>;

export interface SyncServiceWorkerOptions {
  /** False in builds without the PWA plugin (VITE_DISABLE_PWA=true, the Android shell). */
  pwaEnabled: boolean;
  /** Running inside the native kiosk shell (src/platform/kioskHost.ts hasNativeHost()). */
  hostPresent: boolean;
  /** `navigator.serviceWorker`, undefined where unsupported. */
  container: ServiceWorkerApi | undefined;
  /** Runs `fn` once the page has loaded (the plugin registered on `load`). */
  whenLoaded: (fn: () => void) => void;
}

export function syncServiceWorker({ pwaEnabled, hostPresent, container, whenLoaded }: SyncServiceWorkerOptions): void {
  if (!container) return;
  if (hostPresent) {
    container
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .catch(() => {
        // Nothing else to do: the shell's own wipe removes workers too.
      });
    return;
  }
  if (!pwaEnabled) return;
  whenLoaded(() => {
    container.register('/sw.js', { scope: '/' }).catch(() => {
      // Same as before: a failed registration only means no offline cache.
    });
  });
}
